'use client';

import React from 'react';
import { 
  AISummarizeTitleDialog,
  AIGenerateContentDialog,
  AIRefineContentDialog,
  AISchemaDialog,
  AIGenerateImageDialog
} from './ai-dialogs';

export type AICommandType = 'generate' | 'slash-generate' | 'refine' | 'schema' | 'summarize-title' | 'generate-image' | null;

interface AICommandDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (commandType: AICommandType, params: Record<string, any>) => Promise<string | { titles: string[]; reasoning?: string }>;
  commandType: AICommandType;
  initialContent?: string;
  hasSelection?: boolean;
  selectedText?: string;
  onAccept?: (commandType: AICommandType, content: string) => void;
  aiService?: any; // Reference to AICommandService
  articleId?: string; // Article ID for fetching full content
  articleTitle?: string; // Article title for context
}

/**
 * AI Command Dialog that delegates to specialized dialog components based on command type
 */
export default function AICommandDialog({
  isOpen,
  onClose,
  onSubmit,
  commandType,
  initialContent = '',
  hasSelection = false,
  selectedText = '',
  onAccept,
  aiService,
  articleId,
  articleTitle
}: AICommandDialogProps) {
  // Handle submission with the correct command type
  const handleSummarizeTitleSubmit = async (params: Record<string, any>): Promise<{ titles: string[]; reasoning?: string }> => {
    const result = await onSubmit(commandType, params);
    if (typeof result === 'string') {
      // Handle unexpected string result
      return { titles: [result], reasoning: 'Unexpected result format' };
    }
    return result;
  };

  const handleContentSubmit = async (params: Record<string, any>): Promise<string> => {
    const result = await onSubmit(commandType, params);
    if (typeof result === 'string') {
      return result;
    }
    // Handle unexpected object result
    return JSON.stringify(result);
  };

  // Handle accepting the result
  const handleAccept = (content: string) => {
    if (onAccept && commandType) {
      onAccept(commandType, content);
    }
  };

  // Render the appropriate dialog based on command type
  switch (commandType) {
    case 'summarize-title':
      return (
        <AISummarizeTitleDialog
          isOpen={isOpen}
          onClose={onClose}
          onSubmit={handleSummarizeTitleSubmit}
          initialContent={initialContent}
          selectedText={selectedText}
          onAccept={handleAccept}
          aiService={aiService}
        />
      );

    case 'generate':
      return (
        <AIGenerateContentDialog
          isOpen={isOpen}
          onClose={onClose}
          onSubmit={handleContentSubmit}
          initialContent={initialContent}
          hasSelection={hasSelection}
          selectedText={selectedText}
          onAccept={handleAccept}
          aiService={aiService}
        />
      );

    case 'refine':
      return (
        <AIRefineContentDialog
          isOpen={isOpen}
          onClose={onClose}
          onSubmit={handleContentSubmit}
          initialContent={initialContent}
          hasSelection={hasSelection}
          selectedText={selectedText}
          onAccept={handleAccept}
          aiService={aiService}
        />
      );

    case 'schema':
      return (
        <AISchemaDialog
          isOpen={isOpen}
          onClose={onClose}
          onSubmit={handleContentSubmit}
          initialContent={initialContent}
          onAccept={handleAccept}
        />
      );

    case 'generate-image':
      return (
        <AIGenerateImageDialog
          isOpen={isOpen}
          onClose={onClose}
          onSubmit={handleContentSubmit}
          initialContent={initialContent}
          selectedText={selectedText}
          onAccept={handleAccept}
          aiService={aiService}
          articleId={articleId}
          articleTitle={articleTitle}
        />
      );

    default:
      return null;
  }
}
